import React, { useState } from 'react';
import { 
    Typography, 
    Button, 
    Popover, 
    List, 
    ListItem, 
    ListItemText, 
    Divider, 
    IconButton, 
    Box, 
    CircularProgress,
    TextField,
    Snackbar,
    Alert,
    useTheme,
    useMediaQuery,
    Avatar,
    Paper,
    Fade,
    Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import placeholderImage from '../../assets/profileplaceholder.webp';
import { addProfileData, addProfileImage, updateUserImage } from '../Queries';
import { updateCorporateUserPhoto } from '../Corporate/CorporateQueries';
import { logout as jwtLogout } from '../../services/authApi';
import { 
    Edit, 
    GitHub, 
    LinkedIn, 
    Save, 
    Cancel,
    Logout,
    Person,
    Email,
    CameraAlt
} from '@mui/icons-material';

const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';

const Profile = ({ anchorEl, handleClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    const [currentImageUrl, setCurrentImageUrl] = useState(localStorage.getItem('profile_image'));
    const [showEditIcon, setShowEditIcon] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [linkedinUrl, setLinkedinUrl] = useState(localStorage.getItem('linkedin_url') || '');
    const [githubUrl, setGithubUrl] = useState(localStorage.getItem('git_url') || '');
    const [isEditingLinkedIn, setIsEditingLinkedIn] = useState(false);
    const [isEditingGithub, setIsEditingGithub] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const user = {
        fname: localStorage.getItem('fname') || '',
        lname: localStorage.getItem('lname') || '',
        email: localStorage.getItem('user') || '',
        imageUrl: currentImageUrl
    };

    const showSnackbar = (message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleLogout = () => {
        try {
            jwtLogout(); // clear access/refresh tokens
        } catch {}
        try { sessionStorage.clear(); } catch {}
        localStorage.clear();
        showSnackbar('Logging out...', 'info');
        setTimeout(() => {
            window.location.href = '/';
        }, 500);
    };

    const handleEditClick = () => {
        document.getElementById('upload-image').click();
    };

    const updateProfileImage = async (imageUrl) => {
        try {
            if (isCorporateMode) {
                // Update corporate_users table
                await updateCorporateUserPhoto(user.email, imageUrl, user.email);
            } else {
                // Update regular users table
                await updateUserImage(user.email, imageUrl);
            }
            showSnackbar('Profile image updated successfully!', 'success');
        } catch (error) {
            console.error('Error updating profile image:', error);
            showSnackbar('Failed to update profile image', 'error');
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showSnackbar('File size should be less than 5MB', 'error');
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            showSnackbar('Please select a valid image file', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);

            fileReader.onload = async () => {
                const imageData = fileReader.result.split(',')[1];
                let response = await addProfileImage(`${user.email}.jpg`, imageData);
                
                if (response && response.data) {
                    // Check if response has statusCode (old format) or direct message (new format)
                    if (response.data.statusCode) {
                        // Old format with statusCode and body
                        const statusCode = response.data.statusCode;
                        const body = typeof response.data.body === 'string' 
                            ? JSON.parse(response.data.body) 
                            : response.data.body;
                        
                        if (statusCode === 200) {
                            localStorage.setItem("profile_image", body.imageUrl);
                            setCurrentImageUrl(body.imageUrl);
                            updateProfileImage(body.imageUrl);
                            showSnackbar('Image uploaded successfully', 'success');
                        } else {
                            showSnackbar('Failed to upload image', 'error');
                        }
                    } else if (response.data.message && response.data.imageUrl) {
                        // New format with direct message and imageUrl
                        localStorage.setItem("profile_image", response.data.imageUrl);
                        setCurrentImageUrl(response.data.imageUrl);
                        updateProfileImage(response.data.imageUrl);
                        showSnackbar('Image uploaded successfully', 'success');
                    } else {
                        showSnackbar('Failed to upload image', 'error');
                    }
                } else {
                    showSnackbar('Failed to upload image', 'error');
                }
                setIsLoading(false);
            };

            fileReader.onerror = () => {
                setIsLoading(false);
                showSnackbar('Error reading file', 'error');
            };
        } catch (error) {
            setIsLoading(false);
            showSnackbar('Unable to upload image, please try again later!', 'error');
            console.error('Error reading file:', error);
        }
    };

    const handleProfileSave = async (input) => {
        try {
            let response = await addProfileData(user.email, githubUrl, linkedinUrl, input);
            if (response.data && response.data.statusCode === 200) {
                showSnackbar('Profile updated successfully!', 'success');
            } else {
                showSnackbar('Failed to save profile', 'error');
            }
        } catch (error) {
            showSnackbar('Unable to save, please try again later!', 'error');
            console.error('Error saving profile:', error);
        }
    };

    const handleSaveLinkedIn = () => {
        if (linkedinUrl && !linkedinUrl.includes('linkedin.com')) {
            showSnackbar('Please enter a valid LinkedIn URL', 'warning');
            return;
        }
        setIsEditingLinkedIn(false);
        localStorage.setItem('linkedin_url', linkedinUrl);
        handleProfileSave("linkedin");
    };

    const handleSaveGithub = () => {
        if (githubUrl && !githubUrl.includes('github.com')) {
            showSnackbar('Please enter a valid GitHub URL', 'warning');
            return;
        }
        setIsEditingGithub(false);
        localStorage.setItem('git_url', githubUrl);
        handleProfileSave("git");
    };

    const handleCancelLinkedIn = () => {
        setIsEditingLinkedIn(false);
        setLinkedinUrl(localStorage.getItem('linkedin_url') || '');
    };

    const handleCancelGithub = () => {
        setIsEditingGithub(false);
        setGithubUrl(localStorage.getItem('git_url') || '');
    };

    const closeProfile = () => {
        handleClose();
        setIsEditingGithub(false);
        setIsEditingLinkedIn(false);
    };

    const popoverWidth = isMobile ? '90vw' : isTablet ? '400px' : '450px';
    const maxPopoverWidth = isMobile ? '350px' : '450px';

    return (
        <>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={closeProfile}
                anchorOrigin={{ 
                    vertical: 'bottom', 
                    horizontal: isMobile ? 'center' : 'right' 
                }}
                transformOrigin={{ 
                    vertical: 'top', 
                    horizontal: isMobile ? 'center' : 'right' 
                }}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        borderRadius: 3,
                        border: 'none',
                        backgroundColor: 'background.paper',
                        width: popoverWidth,
                        maxWidth: maxPopoverWidth,
                        maxHeight: isMobile ? '90vh' : '80vh',
                        overflow: 'auto'
                    }
                }}
                TransitionComponent={Fade}
                transitionDuration={200}
            >
                <Paper sx={{ 
                    p: isMobile ? 2 : 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                }}>
                    {/* Header */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 2,
                        pb: 1,
                        borderBottom: '2px solid',
                        borderColor: 'primary.main'
                    }}>
                        <Typography 
                            variant={isMobile ? "h6" : "h5"} 
                            sx={{ 
                                fontWeight: 'bold',
                                color: '#0a0068',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <Person />
                            Profile
                        </Typography>
                        <Tooltip title="Close">
                            <IconButton 
                                onClick={closeProfile} 
                                size="small"
                                sx={{ 
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Profile Image Section */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        mb: 3 
                    }}>
                        <Box
                            sx={{
                                position: 'relative',
                                mb: 2
                            }}
                            onMouseEnter={() => setShowEditIcon(true)}
                            onMouseLeave={() => setShowEditIcon(false)}
                        >
                            <Avatar
                                sx={{
                                    width: isMobile ? 80 : 100,
                                    height: isMobile ? 80 : 100,
                                    border: '4px solid',
                                    borderColor: 'primary.main',
                                    boxShadow: 3
                                }}
                                src={currentImageUrl || placeholderImage}
                                alt={`${user.fname} ${user.lname}`}
                                key={currentImageUrl} // Force re-render when image changes
                            >
                                {!currentImageUrl && `${user.fname?.[0] || ''}${user.lname?.[0] || ''}`}
                            </Avatar>

                            {(showEditIcon || isMobile) && !isLoading && (
                                <Tooltip title="Change profile picture">
                                    <IconButton
                                        onClick={handleEditClick}
                                        sx={{
                                            position: 'absolute',
                                            bottom: -8,
                                            right: -8,
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            width: 32,
                                            height: 32,
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            },
                                            boxShadow: 2
                                        }}
                                        size="small"
                                    >
                                        <CameraAlt fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {isLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                        borderRadius: '50%',
                                        p: 1
                                    }}
                                >
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </Box>

                        <Typography 
                            variant={isMobile ? "h6" : "h5"} 
                            sx={{ 
                                fontWeight: 'bold',
                                textAlign: 'center',
                                color: 'text.primary'
                            }}
                        >
                            {user.fname} {user.lname}
                        </Typography>
                    </Box>

                    {/* File input for image upload */}
                    <input
                        type="file"
                        id="upload-image"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageUpload}
                    />

                    {/* Profile Information */}
                    <List sx={{ 
                        width: '100%',
                        bgcolor: 'rgba(255,255,255,0.7)',
                        borderRadius: 2,
                        mb: 2
                    }}>
                        {/* Email */}
                        <ListItem sx={{ py: 1.5 }}>
                            <Email sx={{ mr: 2, color: 'text.secondary' }} />
                            <ListItemText 
                                primary={
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                    >
                                        Email
                                    </Typography>
                                }
                                secondary={
                                    <Typography 
                                        variant="body1"
                                        sx={{ 
                                            wordBreak: 'break-all',
                                            fontSize: isMobile ? '0.875rem' : '1rem'
                                        }}
                                    >
                                        {user.email}
                                    </Typography>
                                }
                            />
                        </ListItem>

                        {/* LinkedIn - Hidden in corporate mode */}
                        {!isCorporateMode && (
                            <>
                                <Divider />
                                <ListItem sx={{ py: 1.5 }}>
                                    <LinkedIn sx={{ mr: 2, color: '#0077B5' }} />
                                    <ListItemText sx={{ flex: 1 }}>
                                        {isEditingLinkedIn ? (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="https://linkedin.com/in/yourprofile"
                                        value={linkedinUrl}
                                        onChange={(e) => setLinkedinUrl(e.target.value)}
                                        variant="outlined"
                                        sx={{ mt: 1 }}
                                    />
                                ) : (
                                    <>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                        >
                                            LinkedIn
                                        </Typography>
                                        <Typography 
                                            variant="body1"
                                            sx={{ 
                                                fontSize: isMobile ? '0.875rem' : '1rem'
                                            }}
                                        >
                                            {linkedinUrl ? (
                                                <a 
                                                    href={linkedinUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{ 
                                                        color: '#0077B5', 
                                                        textDecoration: 'none',
                                                        wordBreak: 'break-all'
                                                    }}
                                                >
                                                    {linkedinUrl}
                                                </a>
                                            ) : (
                                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                    Not set
                                                </span>
                                            )}
                                        </Typography>
                                    </>
                                )}
                            </ListItemText>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {isEditingLinkedIn ? (
                                    <>
                                        <Tooltip title="Save">
                                            <IconButton 
                                                onClick={handleSaveLinkedIn}
                                                color="primary"
                                                size="small"
                                            >
                                                <Save />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cancel">
                                            <IconButton 
                                                onClick={handleCancelLinkedIn}
                                                color="secondary"
                                                size="small"
                                            >
                                                <Cancel />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <Tooltip title="Edit LinkedIn">
                                        <IconButton 
                                            onClick={() => setIsEditingLinkedIn(true)}
                                            size="small"
                                        >
                                            <Edit />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                    </Box>
                                </ListItem>
                            </>
                        )}

                        <Divider />

                        {/* GitHub */}
                        {/* <ListItem sx={{ py: 1.5 }}>
                            <GitHub sx={{ mr: 2, color: '#333' }} />
                            <ListItemText sx={{ flex: 1 }}>
                                {isEditingGithub ? (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="https://github.com/yourusername"
                                        value={githubUrl}
                                        onChange={(e) => setGithubUrl(e.target.value)}
                                        variant="outlined"
                                        sx={{ mt: 1 }}
                                    />
                                ) : (
                                    <>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                        >
                                            GitHub
                                        </Typography>
                                        <Typography 
                                            variant="body1"
                                            sx={{ 
                                                fontSize: isMobile ? '0.875rem' : '1rem'
                                            }}
                                        >
                                            {githubUrl ? (
                                                <a 
                                                    href={githubUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{ 
                                                        color: '#333', 
                                                        textDecoration: 'none',
                                                        wordBreak: 'break-all'
                                                    }}
                                                >
                                                    {githubUrl}
                                                </a>
                                            ) : (
                                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                    Not set
                                                </span>
                                            )}
                                        </Typography>
                                    </>
                                )}
                            </ListItemText>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {isEditingGithub ? (
                                    <>
                                        <Tooltip title="Save">
                                            <IconButton 
                                                onClick={handleSaveGithub}
                                                color="primary"
                                                size="small"
                                            >
                                                <Save />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cancel">
                                            <IconButton 
                                                onClick={handleCancelGithub}
                                                color="secondary"
                                                size="small"
                                            >
                                                <Cancel />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <Tooltip title="Edit GitHub">
                                        <IconButton 
                                            onClick={() => setIsEditingGithub(true)}
                                            size="small"
                                        >
                                            <Edit />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </ListItem> */}
                    </List>

                    {/* Logout Button */}
                    <Button
                        fullWidth
                        variant="contained"
                        color="error"
                        onClick={handleLogout}
                        startIcon={<Logout />}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: isMobile ? '0.875rem' : '1rem',
                            fontWeight: 'bold',
                            boxShadow: 2,
                            '&:hover': {
                                boxShadow: 4
                            }
                        }}
                    >
                        Logout
                    </Button>
                </Paper>
            </Popover>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ 
                    vertical: isMobile ? 'top' : 'bottom', 
                    horizontal: 'center' 
                }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Profile;